import {
  groupBy,
  mapKeys,
  uniq,
  omit
} from 'lodash';
import {
  ALL_FUNDS_SUBSCRIPTION_TYPE_ID,
  DAILY_PRICES_TYPE_ID,
  MONTHLY_FUND_TYPE_ID,
  MY_FUNDS_SUBSCRIPTION_TYPE_ID,
  SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID
} from 'services/Preferences';
import {
  MUTUAL_FUND,
  NATIONAL_529,
  MARYLAND_529,
  ALASKA_529,
  productMap,
  TRAC_PLAN,
  ACTIVE_PLUS,
  ACTIVE_ADVISORY
} from 'services/Products';

export const getFundsByRadioSelection = (typeId, selectedTrpCodes, mutualFunds, userMutualFundsWithIVData) => {
  switch (typeId) {
    case MY_FUNDS_SUBSCRIPTION_TYPE_ID:
      return userMutualFundsWithIVData.map((mf) => mf.trpCode);
    case ALL_FUNDS_SUBSCRIPTION_TYPE_ID:
      // Again another strange technical requirement that if all funds is selected we will send null for trp codes and
      // all asset classes
      return null;
    case SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID:
      return uniq(
        selectedTrpCodes
          .map((code) => mutualFunds.find((mf) => mf.trpCode === code))
          .map((fund) => fund.trpCode)
      );
    default:
      return [];
  }
};

export const getBroadAssetsByRadioSelection = (typeId, selectedTrpCodes, mutualFunds) => {
  switch (typeId) {
    case MY_FUNDS_SUBSCRIPTION_TYPE_ID:
      return [70];
    case ALL_FUNDS_SUBSCRIPTION_TYPE_ID:
      return [10, 20, 30, 40, 50];
    case SELECTED_FUNDS_SUBSCRIPTION_TYPE_ID: {
      // this is very strange technical requirement that if not all funds are selected within an asset group we
      // will not send that asset group to the data base. If all funds are selected we will send all asset classes
      const allAssetGroups = groupBy(
        mutualFunds,
        (mf) => mf.broadAssetClassId
      );
      const selectedFundGroups = groupBy(
        selectedTrpCodes.map((code) =>
          mutualFunds.find((mf) => mf.trpCode === code)
        ),
        (mf) => mf.broadAssetClassId
      );
      const assetsForSubmission = [];
      mapKeys(selectedFundGroups, (value, key) => {
        const allFundsOfAssetClass = allAssetGroups[key];
        const allIncluded =
          allFundsOfAssetClass &&
          allFundsOfAssetClass.length === value.length &&
          allFundsOfAssetClass.every((fund) => value.includes(fund));
        if (allIncluded) {
          assetsForSubmission.push(parseInt(key));
        }
      });
      return assetsForSubmission;
    }
    default:
      return [];
  }
};

export const EMPTY_SELECTION = {
  typeId: -1,
  broadAssetClassIds: [],
  trpCodes: []
};

export const generateDailyPricesPayload = (priceTypeSelected, priceAssetClasses, priceCodes, newsLetterIds) => {
  return {
    digitalSubscriptions: {
      priceSubscriptions: {
        typeId: priceTypeSelected,
        broadAssetClassIds: priceAssetClasses,
        trpCodes: priceCodes
      },
      newsLetterIds: uniq(newsLetterIds.concat(DAILY_PRICES_TYPE_ID))
    }
  };
};

export const dailyPricesEmptySelection = newsLetterIds => {
  return {
    digitalSubscriptions: {
      priceSubscriptions: EMPTY_SELECTION,
      newsLetterIds: newsLetterIds.filter(
        (id) => DAILY_PRICES_TYPE_ID !== id
      )
    }
  };
};

export const generateMonthlyFundPerformancePayload = (performanceTypeSelected, performanceAssetClasses, performanceCodes, newsLetterIds) => {
  return {
    digitalSubscriptions: {
      performanceSubscriptions: {
        typeId: performanceTypeSelected,
        broadAssetClassIds: performanceAssetClasses,
        trpCodes: performanceCodes
      },
      newsLetterIds: uniq(newsLetterIds.concat(MONTHLY_FUND_TYPE_ID))
    }
  };
};

export const monthlyFundPerformanceEmptySelection = newsLetterIds => {
  return {
    digitalSubscriptions: {
      performanceSubscriptions: EMPTY_SELECTION,
      newsLetterIds: newsLetterIds.filter(
        (id) => MONTHLY_FUND_TYPE_ID !== id
      )
    }
  };
};

export const generateSubscriptionsPayload = (initialSubscriptions, subscriptions) => {
  const { optOutElections: updatedOptOutElections = {} } = subscriptions;
  const { optOutElections: initialOptOutElections = {} } = initialSubscriptions;

  //document delivery should be the only updater of compliance subscriptions
  const complianceRemoved = omit(subscriptions, [
    'complianceSubscriptions'
  ]);

  //business requirement to only send in the elections that have changed
  return {
    ...complianceRemoved,
    disposition: { action: 'update' },
    optOutElectionPreferences: getOnlyChangedElections(initialOptOutElections, updatedOptOutElections)
  }
};

//Comparison with what comes in the GET call and the updated elections - only pull updated
export const getOnlyChangedElections = (initialOptOutElections, updatedOptOutElections) => {
  const updatedElections = updatedOptOutElections.filter(e1 => initialOptOutElections.find(e2 => e1.categoryId === e2.categoryId && e1.optOut !== e2.optOut));
  return updatedElections.map(e => omit(e, ['productType']));
};

//Filter elections based on products the user owns
export const getOwnedOptOutElections = (elections, products) => {
  let filteredElections = [];
  const productNameToNumber = products.map(p => productMap[p]);
  const hasTracOnly = productNameToNumber.every(p => p === TRAC_PLAN);
  const hasAPP = productNameToNumber.some(p => p === ACTIVE_PLUS);
  const hasRAS = productNameToNumber.some(p => p === ACTIVE_ADVISORY);
  const hasNoMF = !productNameToNumber.some(p => p === MUTUAL_FUND);
  filteredElections = (elections.filter(e => productNameToNumber.includes(e.productType)));
  if (hasTracOnly || ((hasAPP || hasRAS) && hasNoMF)) {
    elections.filter(e => e.productType === MUTUAL_FUND).forEach(el=> filteredElections.push(el));
  }
  return filteredElections;
};

//Map category name to product type id
export const getProductTypeFromElectionName = election => {
  if(election) {
    const { categoryName } = election;
    if(categoryName) {
      if(categoryName.includes('II')) {
        return MUTUAL_FUND;
      }

      if(categoryName.includes('NA529')) {
        return NATIONAL_529;
      }

      if(categoryName.includes('MD529')) {
        return MARYLAND_529;
      }

      if(categoryName.includes('AK529')) {
        return ALASKA_529;
      }
    }
  }
};
